import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  ErrorPanel,
  Loading,
  PageHeader,
} from "../components/ResourceView";
import { JobSharedAttachments } from "../components/JobSharedAttachments";
import {
  operationsJobsApi,
  type JobPhase,
  type JobServiceDispatchItem,
  type JobServiceDispatchWorkspace,
  type NationalCarrierDispatchBoard,
  type NationalCarrierDispatchItem,
  type OperationsJobDetail,
} from "../lib/operationsJobs";
import {
  operationalCoreApi,
  type CrewAssignment,
  type CrewAvailabilityItem,
  type EmployeeAssignment,
  type EmployeeAvailabilityItem,
  type StaffingWorkspace,
} from "../lib/operationalCore";
import { useRpcResource } from "../lib/useRpcResource";
import "../nationalDispatch.css";

const label = (v: string | null | undefined) =>
  v ? v.replaceAll("_", " ") : "—";
const dt = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString() : "—";
const range = (a: string | null | undefined, b: string | null | undefined) =>
  a && b
    ? `${dt(a)} → ${dt(b)}`
    : a
      ? `From ${dt(a)}`
      : b
        ? `By ${dt(b)}`
        : "Not provided";
const dateRange = (
  a: string | null | undefined,
  b: string | null | undefined,
) =>
  a && b
    ? `${new Date(`${a}T00:00:00`).toLocaleDateString()} → ${new Date(`${b}T00:00:00`).toLocaleDateString()}`
    : (a ?? b ?? "Not provided");
function localInput(v: string | null | undefined) {
  if (!v) return "";
  const d = new Date(v);
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 16);
}
function shifted(v: string | null | undefined, hours: number) {
  if (!v) return "";
  const d = new Date(v);
  d.setHours(d.getHours() + hours);
  return localInput(d.toISOString());
}
function seedStart(p: JobPhase) {
  return (
    localInput(p.scheduled_start || p.required_window_start) ||
    shifted(p.scheduled_end || p.required_window_end, -2)
  );
}
function seedEnd(p: JobPhase) {
  return (
    localInput(p.scheduled_end || p.required_window_end) ||
    shifted(p.scheduled_start || p.required_window_start, 2)
  );
}
function phaseLabel(p: JobPhase) {
  if (p.phase_code === "pickup_teardown" || p.phase_type === "teardown")
    return "Pickup / Teardown";
  if (p.phase_code === "receiving_inspection" || p.phase_type === "inspection")
    return "Receiving / Inspection";
  const n: Record<string, string> = {
    warehouse_prep: "Warehouse Prep",
    loading: "Loading",
    delivery: "Outbound Delivery",
    installation: "Installation",
    on_rent: "On Rent",
    return_transport: "Return Transport",
  };
  return n[p.phase_type] ?? label(p.phase_type);
}
function phaseGroup(p: JobPhase) {
  if (["warehouse_prep", "loading", "inspection"].includes(p.phase_type))
    return "Warehouse";
  if (["delivery", "return_transport"].includes(p.phase_type))
    return "National Freight";
  if (["installation", "on_rent", "teardown"].includes(p.phase_type))
    return "National Field Operations";
  return "Operations";
}
function visiblePhases(phases: JobPhase[]) {
  return phases.filter(
    (p) =>
      !["pickup", "receiving"].includes(p.phase_code) &&
      p.phase_type !== "service_call" &&
      p.status !== "skipped",
  );
}
function terminal(p: JobPhase) {
  return ["completed", "cancelled", "skipped"].includes(p.status);
}
function freightPhase(p: JobPhase) {
  return p.phase_type === "delivery" || p.phase_type === "return_transport";
}
function assignedCrews(p: JobPhase, s: StaffingWorkspace | null | undefined) {
  return (
    s?.crew_assignments.filter(
      (a) =>
        a.phase_id === p.id && ["assigned", "completed"].includes(a.status),
    ) ?? []
  );
}
function carrierFor(items: NationalCarrierDispatchItem[], phaseId: string) {
  return items.find((x) => x.phase_id === phaseId);
}
function scheduled(
  p: JobPhase,
  s: StaffingWorkspace | null | undefined,
  carriers: NationalCarrierDispatchItem[],
  onRentCrewRequired = false,
) {
  if (terminal(p)) return true;
  if (!p.scheduled_start || !p.scheduled_end) return false;
  if (freightPhase(p))
    return Boolean(carrierFor(carriers, p.id)?.carrier_scheduled);
  if (p.phase_type === "on_rent" && !onRentCrewRequired) return true;
  return Boolean(
    s &&
    (s.crew_assignments.some(
      (a) => a.phase_id === p.id && a.status === "assigned",
    ) ||
      s.employee_assignments.some(
        (a) => a.phase_id === p.id && a.status === "assigned",
      )),
  );
}
function planningChecked(
  p: JobPhase,
  s: StaffingWorkspace | null | undefined,
  carriers: NationalCarrierDispatchItem[],
  onRentCrewRequired = false,
) {
  if (terminal(p)) return true;
  if (freightPhase(p) || (p.phase_type === "on_rent" && !onRentCrewRequired))
    return Boolean(p.dispatch_planning_completed_at);
  return scheduled(p, s, carriers, onRentCrewRequired);
}

type Tab = "overview" | "schedule" | "activity" | "service" | "attachments";
type RunAction = () => Promise<{
  data: unknown;
  error: { message: string } | null;
}>;
type Run = (action: RunAction, success: string) => Promise<void>;

export function NationalOperationsJobDetailPage() {
  const { jobId } = useParams();
  const detailR = useRpcResource(
    () =>
      jobId
        ? operationsJobsApi.detail(jobId)
        : Promise.resolve({
            data: null,
            error: { message: "Job id is missing." },
          }),
    [jobId],
  );
  const staffingR = useRpcResource(
    () =>
      jobId
        ? operationalCoreApi.staffing(jobId)
        : Promise.resolve({ data: null, error: null }),
    [jobId],
  );
  const orgId = detailR.data?.job.organization_id ?? null;
  const carrierR = useRpcResource<NationalCarrierDispatchBoard>(
    () =>
      orgId
        ? operationsJobsApi.nationalCarrierBoard(orgId)
        : Promise.resolve({ data: null, error: null }),
    [orgId],
  );
  const serviceR = useRpcResource<JobServiceDispatchWorkspace>(
    () =>
      jobId
        ? operationsJobsApi.serviceDispatch(jobId)
        : Promise.resolve({ data: null, error: null }),
    [jobId],
  );
  const [tab, setTab] = useState<Tab>("overview");
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  async function run(action: RunAction, success: string) {
    const scrollTop = window.scrollY;
    setWorking(true);
    setMessage(null);
    setActionError(null);
    const x = await action();
    if (x.error) setActionError(x.error.message);
    else {
      setMessage(success);
      await Promise.all([
        detailR.reload(),
        staffingR.reload(),
        carrierR.reload(),
        serviceR.reload(),
      ]);
    }
    setWorking(false);
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        window.scrollTo({ top: scrollTop, behavior: "auto" }),
      ),
    );
  }
  if (detailR.loading && !detailR.data) return <Loading />;
  if (detailR.error)
    return (
      <>
        <PageHeader title="National Job & Dispatch" />
        <ErrorPanel message={detailR.error} />
      </>
    );
  if (!detailR.data)
    return <ErrorPanel message="National Job did not return data." />;
  const data = detailR.data,
    j = data.job,
    phases = visiblePhases(data.phases),
    staffing = staffingR.data,
    carriers = (carrierR.data?.items ?? []).filter((x) => x.job_id === j.id);
  return (
    <>
      <PageHeader
        title={j.job_name}
        subtitle={`${j.account_name ?? "Customer"} · Ref ${j.job_number} · National Operations & Dispatch`}
        actions={
          <>
            <Link className="button-link" to="/national/jobs?view=resources">
              Crew & Personnel Board
            </Link>
            <Link className="button-link" to="/national/field">
              National Field Operations
            </Link>
            <Link className="button-link" to="/national/jobs">
              Back to National Jobs
            </Link>
            <button
              onClick={() =>
                void Promise.all([
                  detailR.reload(),
                  staffingR.reload(),
                  carrierR.reload(),
                  serviceR.reload(),
                ])
              }
            >
              Refresh
            </button>
          </>
        }
      />
      {message && <div className="notice notice-success">{message}</div>}
      {actionError && <ErrorPanel message={actionError} />}{" "}
      {carrierR.error && (
        <div className="notice notice-warning">
          Carrier scheduling could not load: {carrierR.error}
        </div>
      )}
      <div className="metric-grid ops-metrics national-requested-windows">
        <Metric label="Job status" value={label(j.status)} />
        <Metric label="Priority" value={label(j.priority)} />
        <Metric
          label="Customer requested ship / delivery"
          value={range(j.delivery_window_start, j.delivery_window_end)}
        />
        <Metric
          label="Customer requested install"
          value={range(j.install_window_start, j.install_window_end)}
        />
        <Metric
          label="Customer requested pickup / teardown"
          value={range(j.pickup_window_start, j.pickup_window_end)}
        />
        <Metric
          label="Rental dates"
          value={dateRange(j.rental_start_date, j.rental_end_date)}
        />
      </div>
      <nav className="job-tabs" aria-label="National Job workspace">
        <button
          className={tab === "overview" ? "active" : ""}
          onClick={() => setTab("overview")}
        >
          Overview
        </button>
        <button
          className={tab === "schedule" ? "active" : ""}
          onClick={() => setTab("schedule")}
        >
          Schedule & Crews
        </button>
        <button
          className={tab === "activity" ? "active" : ""}
          onClick={() => setTab("activity")}
        >
          Activity
        </button>
        <button
          className={tab === "service" ? "active" : ""}
          onClick={() => setTab("service")}
        >
          Service Calls
          {serviceR.data?.items.length
            ? ` (${serviceR.data.items.length})`
            : ""}
        </button>
        <button
          className={tab === "attachments" ? "active" : ""}
          onClick={() => setTab("attachments")}
        >
          Attachments
        </button>
      </nav>
      {tab === "overview" && (
        <Overview data={data} working={working} run={run} />
      )}
      {tab === "schedule" &&
        ((staffingR.loading && !staffingR.data) ||
        (carrierR.loading && !carrierR.data) ? (
          <Loading />
        ) : staffingR.error ? (
          <ErrorPanel message={staffingR.error} />
        ) : staffing ? (
          <Schedule
            jobId={j.id}
            phases={phases}
            staffing={staffing}
            carriers={carriers}
            carrierManage={Boolean(carrierR.data?.capabilities.manage)}
            canManage={data.capabilities.manage}
            working={working}
            run={run}
            onRentCrewRequired={j.on_rent_crew_required}
            onRentCoverageNotes={j.on_rent_coverage_notes}
          />
        ) : (
          <ErrorPanel message="Staffing data did not return." />
        ))}
      {tab === "activity" && (
        <Activity phases={phases} staffing={staffing} carriers={carriers} />
      )}
      {tab === "service" &&
        ((serviceR.loading && !serviceR.data) ||
        (staffingR.loading && !staffingR.data) ? (
          <Loading />
        ) : serviceR.error ? (
          <ErrorPanel message={serviceR.error} />
        ) : staffing && serviceR.data ? (
          <ServiceCalls
            jobId={j.id}
            items={serviceR.data.items}
            staffing={staffing}
            canAssign={serviceR.data.capabilities.assign}
            working={working}
            run={run}
          />
        ) : (
          <ErrorPanel message="Service Dispatch data did not return." />
        ))}
      {tab === "attachments" && <JobSharedAttachments jobId={j.id} />}
    </>
  );
}

function Metric({ label: caption, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <span>{caption}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Overview({
  data,
  working,
  run,
}: {
  data: OperationsJobDetail;
  working: boolean;
  run: Run;
}) {
  const j = data.job;
  return (
    <div className="detail-grid operations-job-grid">
      <div>
        <Card title="National Job & site">
          <dl className="detail-list">
            <dt>Job</dt>
            <dd>{j.job_number}</dd>
            <dt>Customer</dt>
            <dd>{j.account_name ?? "—"}</dd>
            <dt>Fulfillment</dt>
            <dd>{j.fulfillment_location ?? "—"}</dd>
            <dt>Ship / install site</dt>
            <dd>{j.site_name ?? "—"}</dd>
            <dt>Address</dt>
            <dd>
              {[
                j.site_address_line1,
                j.site_city,
                j.site_state_region,
                j.site_postal_code,
              ]
                .filter(Boolean)
                .join(", ") || "—"}
            </dd>
            <dt>Site contact</dt>
            <dd>
              {j.site_contact_name ?? "—"}
              {j.site_contact_phone ? ` · ${j.site_contact_phone}` : ""}
            </dd>
            <dt>Requested ship / delivery</dt>
            <dd>{range(j.delivery_window_start, j.delivery_window_end)}</dd>
            <dt>Requested install</dt>
            <dd>{range(j.install_window_start, j.install_window_end)}</dd>
            <dt>On Rent crew</dt>
            <dd>
              {j.on_rent_crew_required
                ? `Required · ${range(j.on_rent_coverage_start, j.on_rent_coverage_end)}`
                : "Not required by Sales"}
            </dd>
            {j.on_rent_coverage_notes && (
              <>
                <dt>On Rent instructions</dt>
                <dd>{j.on_rent_coverage_notes}</dd>
              </>
            )}
            <dt>Requested pickup / teardown</dt>
            <dd>{range(j.pickup_window_start, j.pickup_window_end)}</dd>
          </dl>
        </Card>
        <Card title="Order scope / What this Job includes">
          {data.order_items.length === 0 ? (
            <div className="empty-state">
              No Order items are attached to this Job.
            </div>
          ) : (
            <div className="job-order-scope">
              {data.order_items.map((item) => (
                <OrderScopeItem item={item} key={item.line_key} />
              ))}
            </div>
          )}
        </Card>
      </div>
      <ActionPanel data={data} working={working} run={run} />
    </div>
  );
}

function OrderScopeItem({
  item,
}: {
  item: OperationsJobDetail["order_items"][number];
}) {
  const cfg = item.configuration_snapshot ?? {};
  const width = Number(cfg.width_m ?? 0);
  const length = Number(cfg.length_m ?? 0);
  const bays = Number(cfg.bay_count ?? 0);
  const actual = item.bom_components ?? [];
  const reference = item.bom_reference_components ?? [];
  const parts = actual.length ? actual : reference;
  const isReference = actual.length === 0 && reference.length > 0;
  const totalQty = (q: number | null | undefined) =>
    Number(q ?? 0) * Number(item.quantity || 1);
  const grouped = reference.reduce<Record<string, typeof reference>>((a, p) => {
    const key = p.category_name ?? "Other";
    (a[key] ??= []).push(p);
    return a;
  }, {});
  return (
    <div className="job-order-scope-item">
      <div className="job-order-scope-row">
        <div>
          <span className="eyebrow">
            {label(item.line_type)}
            {item.sku ? ` · ${item.sku}` : ""}
          </span>
          <strong>{item.product_name ?? item.description}</strong>
          {item.description && item.description !== item.product_name && (
            <small>{item.description}</small>
          )}
          {width > 0 && length > 0 && (
            <small>
              {Math.round(width / 0.3048)}' × {Math.round(length / 0.3048)}'
              {bays > 0 ? ` · ${bays} bays` : ""}
            </small>
          )}
          {item.notes && <small>{item.notes}</small>}
        </div>
        <strong>Qty {Number(item.quantity).toLocaleString()}</strong>
      </div>
      {parts.length === 0 && (
        <div className="job-bom-note">
          <strong>BOM not linked</strong>
          <span>
            This configured item has no component records in the Order yet.
            Import or link the authoritative Point of Rental package to display
            the complete assembly here.
          </span>
        </div>
      )}
      {parts.length > 0 && (
        <details className="job-bom-details">
          <summary>
            <span>
              <strong>View tent BOM / assembly contents</strong>
              <small>
                {isReference
                  ? `${parts.length} draft component records`
                  : `${parts.length} component${parts.length === 1 ? "" : "s"}`}
              </small>
            </span>
          </summary>
          <div className="job-bom-body">
            {isReference ? (
              <>
                <div className="job-bom-note">
                  <strong>Draft reference components</strong>
                  <span>
                    These component shells show the tent assembly categories we
                    have loaded. Exact Mahaffey / Point of Rental quantities are
                    not linked yet, so they do not create inventory demand or
                    change availability.
                  </span>
                </div>
                {Object.entries(grouped).map(([group, items]) => (
                  <section className="job-bom-group" key={group}>
                    <h4>{group}</h4>
                    {items.map((p) => (
                      <div className="job-bom-row" key={p.product_id}>
                        <div>
                          <strong>{p.name}</strong>
                          <small>{p.sku ?? "No SKU yet"}</small>
                        </div>
                        <span>Qty pending POR</span>
                      </div>
                    ))}
                  </section>
                ))}
              </>
            ) : (
              <>
                {actual.map((p) => (
                  <div
                    className="job-bom-row"
                    key={`${p.product_id}-${p.component_role ?? ""}`}
                  >
                    <div>
                      <strong>{p.name}</strong>
                      <small>
                        {[
                          p.sku,
                          p.component_role ? label(p.component_role) : null,
                          p.is_optional ? "optional" : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    </div>
                    <span>
                      Qty{" "}
                      {totalQty(p.required_quantity_per_unit).toLocaleString(
                        undefined,
                        { maximumFractionDigits: 2 },
                      )}
                      {p.unit_of_measure ? ` ${p.unit_of_measure}` : ""}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

function ActionPanel({
  data,
  working,
  run,
}: {
  data: OperationsJobDetail;
  working: boolean;
  run: Run;
}) {
  const j = data.job;
  const [status, setStatus] = useState(data.allowed_job_transitions[0] ?? "");
  const [reason, setReason] = useState("");
  const [priority, setPriority] = useState(j.priority ?? "normal");
  const [access, setAccess] = useState(j.site_access_notes ?? "");
  const [install, setInstall] = useState(j.install_requirements ?? "");
  const [safety, setSafety] = useState(j.safety_notes ?? "");
  const manage = data.capabilities.manage;
  return (
    <aside className="action-drawer operations-actions">
      <h2>National Dispatch controls</h2>
      <p className="muted">
        Use Schedule & Crews for carrier booking, phase times and field crews.
        Shared Work Order and customer files are kept on the Attachments tab.
      </p>
      {manage && ["planning", "on_hold"].includes(j.status) && (
        <button
          className="primary full"
          disabled={working}
          onClick={() =>
            void run(
              () => operationsJobsApi.release(j.id),
              "Job readiness checked and released to National Dispatch.",
            )
          }
        >
          Check readiness & release
        </button>
      )}
      <div className="action-form">
        <strong>Job status</strong>
        <select
          disabled={!manage || working || !data.allowed_job_transitions.length}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {data.allowed_job_transitions.map((s) => (
            <option key={s} value={s}>
              {label(s)}
            </option>
          ))}
        </select>
        <input
          disabled={!manage || working}
          placeholder="Reason / note"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <button
          disabled={!manage || working || !status}
          onClick={() =>
            void run(
              () =>
                operationsJobsApi.transitionJob(j.id, status, reason || null),
              `Job moved to ${label(status)}.`,
            )
          }
        >
          Apply status
        </button>
      </div>
      <div className="action-form">
        <strong>Operational details</strong>
        <label className="field">
          <span>Priority</span>
          <select
            disabled={!manage || working}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            {["low", "normal", "high", "urgent", "emergency"].map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Site access</span>
          <textarea
            disabled={!manage || working}
            value={access}
            onChange={(e) => setAccess(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Install requirements</span>
          <textarea
            disabled={!manage || working}
            value={install}
            onChange={(e) => setInstall(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Safety notes</span>
          <textarea
            disabled={!manage || working}
            value={safety}
            onChange={(e) => setSafety(e.target.value)}
          />
        </label>
        <button
          disabled={!manage || working}
          onClick={() =>
            void run(
              () =>
                operationsJobsApi.updateDetails(
                  j.id,
                  access || null,
                  install || null,
                  safety || null,
                  priority,
                ),
              "Operational details updated.",
            )
          }
        >
          Save details
        </button>
      </div>
    </aside>
  );
}

function Schedule({
  jobId,
  phases,
  staffing,
  carriers,
  carrierManage,
  canManage,
  working,
  run,
  onRentCrewRequired,
  onRentCoverageNotes,
}: {
  jobId: string;
  phases: JobPhase[];
  staffing: StaffingWorkspace;
  carriers: NationalCarrierDispatchItem[];
  carrierManage: boolean;
  canManage: boolean;
  working: boolean;
  run: Run;
  onRentCrewRequired: boolean;
  onRentCoverageNotes: string | null;
}) {
  const count = phases.filter((p) =>
    planningChecked(p, staffing, carriers, onRentCrewRequired),
  ).length;
  return (
    <Card title="National Operations & Dispatch Checklist">
      <div className="ops-checklist-heading">
        <div>
          <strong>
            {count} of {phases.length} phases planned
          </strong>
          <p className="muted">
            This checklist records planning only and does not advance field
            execution. Outbound Delivery and Return Transport use third-party
            carriers; Installation and Pickup / Teardown use National field
            crews. Crew and individual assignments cannot overlap another Job.
          </p>
        </div>
        <span
          className={`status-pill ${count === phases.length ? "status-passed" : "status-open"}`}
        >
          {count === phases.length ? "All Planning Complete" : "Planning"}
        </span>
      </div>
      <div className="ops-phase-grid">
        {phases.map((p) => (
          <Phase
            key={p.id}
            jobId={jobId}
            phase={p}
            staffing={staffing}
            carrier={carrierFor(carriers, p.id)}
            carrierManage={carrierManage}
            canManage={canManage}
            working={working}
            run={run}
            onRentCrewRequired={onRentCrewRequired}
            onRentCoverageNotes={onRentCoverageNotes}
          />
        ))}
      </div>
    </Card>
  );
}

function Phase({
  jobId,
  phase,
  staffing,
  carrier,
  carrierManage,
  canManage,
  working,
  run,
  onRentCrewRequired,
  onRentCoverageNotes,
}: {
  jobId: string;
  phase: JobPhase;
  staffing: StaffingWorkspace;
  carrier: NationalCarrierDispatchItem | undefined;
  carrierManage: boolean;
  canManage: boolean;
  working: boolean;
  run: Run;
  onRentCrewRequired: boolean;
  onRentCoverageNotes: string | null;
}) {
  const [start, setStart] = useState(seedStart(phase));
  const [end, setEnd] = useState(seedEnd(phase));
  const [crewId, setCrewId] = useState("");
  const [availability, setAvailability] = useState<CrewAvailabilityItem[]>([]);
  const [checking, setChecking] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(
    null,
  );
  const assignments = useMemo(
    () => assignedCrews(phase, staffing),
    [phase.id, staffing.crew_assignments],
  );
  const assignedIds = new Set(assignments.map((a) => a.crew_id));
  useEffect(() => {
    let live = true;
    if (
      freightPhase(phase) ||
      !start ||
      !end ||
      new Date(end) <= new Date(start)
    ) {
      setAvailability([]);
      return () => {
        live = false;
      };
    }
    setChecking(true);
    operationalCoreApi
      .crewAvailability(
        jobId,
        phase.id,
        new Date(start).toISOString(),
        new Date(end).toISOString(),
      )
      .then((x) => {
        if (!live) return;
        if (x.error) {
          setAvailabilityError(x.error.message);
          setAvailability([]);
        } else {
          setAvailabilityError(null);
          setAvailability(x.data?.crews ?? []);
        }
        setChecking(false);
      });
    return () => {
      live = false;
    };
  }, [
    jobId,
    phase.id,
    phase.phase_type,
    start,
    end,
    staffing.crew_assignments.length,
  ]);
  const selectable = availability.filter((c) => !assignedIds.has(c.id));
  const selected = availability.find((c) => c.id === crewId);
  const valid = Boolean(start && end && new Date(end) > new Date(start));
  const planningComplete = Boolean(phase.dispatch_planning_completed_at);
  const setupReady = scheduled(
    phase,
    staffing,
    carrier ? [carrier] : [],
    onRentCrewRequired,
  );
  const isChecked = planningChecked(
    phase,
    staffing,
    carrier ? [carrier] : [],
    onRentCrewRequired,
  );
  const editable = canManage && !working && !terminal(phase);
  const canComplete =
    editable &&
    !planningComplete &&
    (freightPhase(phase) ||
      (phase.phase_type === "on_rent" && !onRentCrewRequired));
  async function addCrew() {
    if (!crewId || !valid) return;
    await run(
      async () => {
        const t = await operationsJobsApi.schedulePhase(
          phase.id,
          new Date(start).toISOString(),
          new Date(end).toISOString(),
        );
        if (t.error) return t;
        return operationalCoreApi.assignCrew(
          jobId,
          phase.id,
          crewId,
          new Date(start).toISOString(),
          new Date(end).toISOString(),
          assignments.length === 0,
        );
      },
      `${selected?.name ?? "Crew"} assigned to ${phaseLabel(phase)}.`,
    );
    setCrewId("");
  }
  async function completePlanning() {
    if (!valid) return;
    await run(
      async () => {
        const saved = await operationsJobsApi.schedulePhase(
          phase.id,
          new Date(start).toISOString(),
          new Date(end).toISOString(),
        );
        if (saved.error) return saved;
        return operationsJobsApi.completePlanning(
          phase.id,
          `Planning completed from National Operations & Dispatch checklist`,
        );
      },
      `${phaseLabel(phase)} planning marked complete.`,
    );
  }
  return (
    <section className={`ops-phase-card ${isChecked ? "ready" : ""}`}>
      <div className="ops-phase-head">
        <span className={`ops-check ${isChecked ? "done" : ""}`}>
          {isChecked ? "✓" : ""}
        </span>
        <div className="ops-phase-title">
          <h3>{phaseLabel(phase)}</h3>
          <span>{phaseGroup(phase)}</span>
          <small>
            Customer requested:{" "}
            {range(phase.required_window_start, phase.required_window_end)}
          </small>
        </div>
        <span
          className={`status-pill ${isChecked ? "status-passed" : "status-open"}`}
        >
          {planningComplete
            ? "Planning complete"
            : terminal(phase)
              ? label(phase.status)
              : setupReady
                ? "Scheduled"
                : "Needs setup"}
        </span>
      </div>
      <div className="ops-time-grid">
        <label className="field">
          <span>Start</span>
          <input
            type="datetime-local"
            disabled={!editable}
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </label>
        <label className="field">
          <span>End</span>
          <input
            type="datetime-local"
            disabled={!editable}
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </label>
        <button
          disabled={!editable || !valid}
          onClick={() =>
            void run(
              () =>
                operationsJobsApi.schedulePhase(
                  phase.id,
                  new Date(start).toISOString(),
                  new Date(end).toISOString(),
                ),
              `${phaseLabel(phase)} time saved.`,
            )
          }
        >
          Save time
        </button>
      </div>
      {freightPhase(phase) ? (
        <CarrierControl
          phase={phase}
          carrier={carrier}
          canManage={carrierManage && editable}
          working={working}
          run={run}
        />
      ) : (
        <>
          <div className="ops-crews">
            <strong>
              {phase.phase_type === "on_rent"
                ? onRentCrewRequired
                  ? "On Rent crew required by Sales"
                  : "No On Rent crew required"
                : "Assigned crew"}
            </strong>
            {phase.phase_type === "on_rent" && onRentCoverageNotes && (
              <div className="notice notice-info">{onRentCoverageNotes}</div>
            )}
            {assignments.length === 0 ? (
              <small className="ops-none">
                {phase.phase_type === "on_rent"
                  ? onRentCrewRequired
                    ? "Assign an available crew for the Sales-requested coverage window."
                    : "Sales confirmed that no dedicated crew must remain on site."
                  : "No crew assigned yet."}
              </small>
            ) : (
              <div className="ops-crew-list">
                {assignments.map((a) => (
                  <CrewRow
                    key={a.id}
                    a={a}
                    editable={editable}
                    canManage={staffing.capabilities.manage}
                    run={run}
                  />
                ))}
              </div>
            )}
            {(phase.phase_type !== "on_rent" || onRentCrewRequired) && (
              <div className="ops-add-crew">
                <select
                  disabled={
                    !editable ||
                    !staffing.capabilities.assign ||
                    !valid ||
                    checking
                  }
                  value={crewId}
                  onChange={(e) => setCrewId(e.target.value)}
                >
                  <option value="">
                    {checking
                      ? "Checking availability…"
                      : "Select available crew"}
                  </option>
                  {selectable.map((c) => (
                    <option key={c.id} value={c.id} disabled={!c.available}>
                      {c.recommended ? "★ " : ""}
                      {c.name} · {label(c.crew_type)} · {c.member_count} members
                      {c.available
                        ? ""
                        : ` · ${c.conflict_reason ?? "Unavailable"}`}
                    </option>
                  ))}
                </select>
                <button
                  className="primary"
                  disabled={
                    !editable || !crewId || !selected?.available || !valid
                  }
                  onClick={() => void addCrew()}
                >
                  Add crew to phase
                </button>
              </div>
            )}
            {availabilityError && (
              <div className="notice notice-warning">{availabilityError}</div>
            )}
          </div>
        </>
      )}{" "}
      {canComplete && (
        <div className="ops-phase-completion">
          <button
            className="primary"
            disabled={working || !valid}
            onClick={() => void completePlanning()}
          >
            Mark {phaseLabel(phase)} Complete
          </button>
          <small>
            This records planning completion only. The times shown above will be
            saved automatically.
          </small>
        </div>
      )}
    </section>
  );
}

function CrewRow({
  a,
  editable,
  canManage,
  run,
}: {
  a: CrewAssignment;
  editable: boolean;
  canManage: boolean;
  run: Run;
}) {
  return (
    <div className="ops-crew-chip">
      <span>
        <strong>{a.crew_name}</strong>
        <small>
          {dt(a.starts_at)} → {dt(a.ends_at)}
        </small>
      </span>
      {a.status === "assigned" && (
        <button
          disabled={!editable || !canManage}
          onClick={() =>
            void run(
              () =>
                operationalCoreApi.releaseCrew(
                  a.id,
                  "Released from National Dispatch",
                ),
              "Crew released from phase.",
            )
          }
        >
          Release
        </button>
      )}
    </div>
  );
}

function CarrierControl({
  phase,
  carrier,
  canManage,
  working,
  run,
}: {
  phase: JobPhase;
  carrier: NationalCarrierDispatchItem | undefined;
  canManage: boolean;
  working: boolean;
  run: Run;
}) {
  const [name, setName] = useState(carrier?.carrier_name ?? "");
  const [confirm, setConfirm] = useState(carrier?.confirmation_number ?? "");
  const [pickup, setPickup] = useState(
    localInput(carrier?.pickup_appointment_at),
  );
  const [delivery, setDelivery] = useState(
    localInput(carrier?.delivery_appointment_at),
  );
  const [bol, setBol] = useState(carrier?.bol_number ?? "");
  const [pro, setPro] = useState(carrier?.pro_number ?? "");
  const [estimate, setEstimate] = useState(
    carrier?.estimated_cost == null ? "" : String(carrier.estimated_cost),
  );
  const [actual, setActual] = useState(
    carrier?.actual_cost == null ? "" : String(carrier.actual_cost),
  );
  const [notes, setNotes] = useState(carrier?.notes ?? "");
  const [confirmed, setConfirmed] = useState(
    Boolean(carrier?.carrier_scheduled),
  );
  useEffect(() => {
    setName(carrier?.carrier_name ?? "");
    setConfirm(carrier?.confirmation_number ?? "");
    setPickup(localInput(carrier?.pickup_appointment_at));
    setDelivery(localInput(carrier?.delivery_appointment_at));
    setBol(carrier?.bol_number ?? "");
    setPro(carrier?.pro_number ?? "");
    setEstimate(
      carrier?.estimated_cost == null ? "" : String(carrier.estimated_cost),
    );
    setActual(carrier?.actual_cost == null ? "" : String(carrier.actual_cost));
    setNotes(carrier?.notes ?? "");
    setConfirmed(Boolean(carrier?.carrier_scheduled));
  }, [
    carrier?.carrier_name,
    carrier?.confirmation_number,
    carrier?.pickup_appointment_at,
    carrier?.delivery_appointment_at,
    carrier?.bol_number,
    carrier?.pro_number,
    carrier?.estimated_cost,
    carrier?.actual_cost,
    carrier?.notes,
    carrier?.carrier_scheduled,
  ]);
  const disabled = !canManage || working;
  return (
    <div className="ops-driver-control national-carrier-inline">
      <div className="ops-driver-current">
        <span>Third-party carrier</span>
        <strong>{carrier?.carrier_name ?? "Not scheduled"}</strong>
        <small>
          {confirmed
            ? "Dispatch confirmed carrier booking"
            : "Carrier booking required for this National freight leg"}
        </small>
      </div>
      <div className="form-grid sales-entry-form">
        <label className="field">
          <span>Carrier / brokerage company *</span>
          <input
            disabled={disabled}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Confirmation / load #</span>
          <input
            disabled={disabled}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Pickup appointment</span>
          <input
            type="datetime-local"
            disabled={disabled}
            value={pickup}
            onChange={(e) => setPickup(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Delivery appointment</span>
          <input
            type="datetime-local"
            disabled={disabled}
            value={delivery}
            onChange={(e) => setDelivery(e.target.value)}
          />
        </label>
        <label className="field">
          <span>BOL #</span>
          <input
            disabled={disabled}
            value={bol}
            onChange={(e) => setBol(e.target.value)}
          />
        </label>
        <label className="field">
          <span>PRO / tracking #</span>
          <input
            disabled={disabled}
            value={pro}
            onChange={(e) => setPro(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Quoted freight cost</span>
          <input
            type="number"
            min="0"
            step="0.01"
            disabled={disabled}
            value={estimate}
            onChange={(e) => setEstimate(e.target.value)}
          />
        </label>
        <label className="field">
          <span>Actual carrier cost</span>
          <input
            type="number"
            min="0"
            step="0.01"
            disabled={disabled}
            value={actual}
            onChange={(e) => setActual(e.target.value)}
          />
        </label>
        <label className="field wide">
          <span>Dispatch / carrier notes</span>
          <textarea
            rows={2}
            disabled={disabled}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        <label className="checkbox-line wide">
          <input
            type="checkbox"
            disabled={disabled}
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <strong>Scheduled with Third-Party Carrier</strong>
        </label>
        <button
          className="primary"
          disabled={disabled || (confirmed && !name.trim())}
          onClick={() =>
            void run(
              () =>
                operationsJobsApi.saveNationalCarrier({
                  phaseId: phase.id,
                  carrierName: name,
                  confirmationNumber: confirm || null,
                  pickupAppointmentAt: pickup
                    ? new Date(pickup).toISOString()
                    : null,
                  deliveryAppointmentAt: delivery
                    ? new Date(delivery).toISOString()
                    : null,
                  bolNumber: bol || null,
                  proNumber: pro || null,
                  estimatedCost: estimate === "" ? null : Number(estimate),
                  actualCost: actual === "" ? null : Number(actual),
                  notes: notes || null,
                  scheduled: confirmed,
                }),
              confirmed
                ? "Carrier booking confirmed for this freight leg."
                : "Carrier details saved.",
            )
          }
        >
          {confirmed ? "Save & Confirm Carrier" : "Save Carrier Details"}
        </button>
      </div>
    </div>
  );
}

function ServiceCalls({
  jobId,
  items,
  staffing,
  canAssign,
  working,
  run,
}: {
  jobId: string;
  items: JobServiceDispatchItem[];
  staffing: StaffingWorkspace;
  canAssign: boolean;
  working: boolean;
  run: Run;
}) {
  return (
    <Card title="Customer Service field requests">
      <div className="ops-checklist-heading">
        <div>
          <strong>
            {items.length} on-site request{items.length === 1 ? "" : "s"}
          </strong>
          <p className="muted">
            Customer Service creates these from the service ticket. Set the
            actual visit window, then assign an available crew or one available
            employee. Conflicting resources cannot be selected.
          </p>
        </div>
        <Link className="button-link" to="/national/jobs?view=resources">
          Open Crew & Personnel Board
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="empty-state">
          No Customer Service repair requests are attached to this Job.
        </div>
      ) : (
        <div className="service-dispatch-task-list">
          {items.map((item) => (
            <ServiceTask
              key={item.id}
              jobId={jobId}
              item={item}
              staffing={staffing}
              canAssign={canAssign}
              working={working}
              run={run}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function ServiceTask({
  jobId,
  item,
  staffing,
  canAssign,
  working,
  run,
}: {
  jobId: string;
  item: JobServiceDispatchItem;
  staffing: StaffingWorkspace;
  canAssign: boolean;
  working: boolean;
  run: Run;
}) {
  const [start, setStart] = useState(
    localInput(item.scheduled_start || item.requested_start),
  );
  const [end, setEnd] = useState(
    localInput(item.scheduled_end || item.requested_end),
  );
  const [mode, setMode] = useState<"crew" | "employee">("crew");
  const [resourceId, setResourceId] = useState("");
  const [crews, setCrews] = useState<CrewAvailabilityItem[]>([]);
  const [employees, setEmployees] = useState<EmployeeAvailabilityItem[]>([]);
  const [checking, setChecking] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(
    null,
  );
  const crewAssignments = staffing.crew_assignments.filter(
    (a) => a.phase_id === item.phase_id && a.status === "assigned",
  );
  const employeeAssignments = staffing.employee_assignments.filter(
    (a) => a.phase_id === item.phase_id && a.status === "assigned",
  );
  const valid = Boolean(start && end && new Date(end) > new Date(start));
  useEffect(() => {
    let live = true;
    if (!valid) {
      setCrews([]);
      setEmployees([]);
      return () => {
        live = false;
      };
    }
    setChecking(true);
    Promise.all([
      operationalCoreApi.crewAvailability(
        jobId,
        item.phase_id,
        new Date(start).toISOString(),
        new Date(end).toISOString(),
      ),
      operationalCoreApi.employeeAvailability(
        jobId,
        item.phase_id,
        new Date(start).toISOString(),
        new Date(end).toISOString(),
      ),
    ]).then(([c, e]) => {
      if (!live) return;
      const err = c.error?.message ?? e.error?.message ?? null;
      setAvailabilityError(err);
      setCrews(c.data?.crews ?? []);
      setEmployees(e.data?.employees ?? []);
      setChecking(false);
    });
    return () => {
      live = false;
    };
  }, [
    jobId,
    item.phase_id,
    start,
    end,
    staffing.crew_assignments.length,
    staffing.employee_assignments.length,
  ]);
  const choices =
    mode === "crew"
      ? crews.filter((c) => !crewAssignments.some((a) => a.crew_id === c.id))
      : employees.filter(
          (e) => !employeeAssignments.some((a) => a.employee_id === e.id),
        );
  const selected = choices.find((x) => x.id === resourceId);
  const editable =
    canAssign &&
    !working &&
    !["completed", "cancelled"].includes(item.phase_status);
  async function assign() {
    if (!resourceId || !valid || !selected?.available) return;
    await run(async () => {
      const s = await operationsJobsApi.schedulePhase(
        item.phase_id,
        new Date(start).toISOString(),
        new Date(end).toISOString(),
      );
      if (s.error) return s;
      return mode === "crew"
        ? operationalCoreApi.assignCrew(
            jobId,
            item.phase_id,
            resourceId,
            new Date(start).toISOString(),
            new Date(end).toISOString(),
            crewAssignments.length === 0,
          )
        : operationalCoreApi.assignEmployee(
            jobId,
            item.phase_id,
            resourceId,
            new Date(start).toISOString(),
            new Date(end).toISOString(),
            "service technician",
          );
    }, `${selected.name} assigned to ${item.ticket_number}.`);
    setResourceId("");
  }
  return (
    <section
      className={`service-dispatch-task ${item.phase_status === "completed" ? "complete" : ""}`}
    >
      <header>
        <div>
          <span className="eyebrow">
            {item.ticket_number} · {label(item.priority)}
          </span>
          <h3>{item.subject}</h3>
          <p>{item.instructions ?? "No field instructions were provided."}</p>
        </div>
        <span className={`status-pill status-${item.phase_status}`}>
          {label(item.phase_status)}
        </span>
      </header>
      <div className="service-dispatch-window">
        <div>
          <span>Customer requested</span>
          <strong>{range(item.requested_start, item.requested_end)}</strong>
        </div>
        <div>
          <span>Dispatch schedule</span>
          <strong>
            {item.scheduled_start
              ? range(item.scheduled_start, item.scheduled_end)
              : "Not scheduled"}
          </strong>
        </div>
      </div>
      <div className="ops-time-grid">
        <label className="field">
          <span>Visit start</span>
          <input
            type="datetime-local"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            disabled={!editable}
          />
        </label>
        <label className="field">
          <span>Visit end</span>
          <input
            type="datetime-local"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            disabled={!editable}
          />
        </label>
        <button
          disabled={!editable || !valid}
          onClick={() =>
            void run(
              () =>
                operationsJobsApi.schedulePhase(
                  item.phase_id,
                  new Date(start).toISOString(),
                  new Date(end).toISOString(),
                ),
              `${item.ticket_number} visit time saved.`,
            )
          }
        >
          Save time
        </button>
      </div>
      <div className="service-dispatch-assigned">
        <strong>Assigned resources</strong>
        {crewAssignments.length === 0 && employeeAssignments.length === 0 ? (
          <small className="ops-none">No crew or employee assigned yet.</small>
        ) : (
          <div className="ops-crew-list">
            {crewAssignments.map((a) => (
              <CrewRow
                key={a.id}
                a={a}
                editable={editable}
                canManage={staffing.capabilities.manage}
                run={run}
              />
            ))}
            {employeeAssignments.map((a) => (
              <EmployeeRow
                key={a.id}
                a={a}
                editable={editable}
                canManage={staffing.capabilities.manage}
                run={run}
              />
            ))}
          </div>
        )}
      </div>
      <div className="service-dispatch-assign">
        <select
          value={mode}
          onChange={(e) => {
            setMode(e.target.value as "crew" | "employee");
            setResourceId("");
          }}
          disabled={!editable}
        >
          <option value="crew">Assign full crew</option>
          <option value="employee">Assign individual employee</option>
        </select>
        <select
          value={resourceId}
          onChange={(e) => setResourceId(e.target.value)}
          disabled={!editable || checking || !valid}
        >
          <option value="">
            {checking ? "Checking availability…" : `Select available ${mode}`}
          </option>
          {choices.map((x) => (
            <option key={x.id} value={x.id} disabled={!x.available}>
              {x.name}
              {"member_count" in x
                ? ` · ${x.member_count} members`
                : x.location_name
                  ? ` · ${x.location_name}`
                  : ""}
              {x.available ? "" : ` · ${x.conflict_reason ?? "Unavailable"}`}
            </option>
          ))}
        </select>
        <button
          className="primary"
          disabled={!editable || !resourceId || !selected?.available}
          onClick={() => void assign()}
        >
          Assign
        </button>
      </div>
      {availabilityError && (
        <div className="notice notice-warning">{availabilityError}</div>
      )}
      <div className="row-actions service-dispatch-actions">
        <Link className="button-link" to={`/service/tickets/${item.ticket_id}`}>
          Open Customer Service Ticket
        </Link>
        {item.phase_status === "scheduled" && (
          <button
            disabled={working}
            onClick={() =>
              void run(
                () =>
                  operationsJobsApi.transitionPhase(
                    item.phase_id,
                    "in_progress",
                    "Crew dispatched from National Operations & Dispatch",
                  ),
                `${item.ticket_number} marked in progress.`,
              )
            }
          >
            Mark In Progress
          </button>
        )}
        {item.phase_status === "in_progress" && (
          <button
            className="primary"
            disabled={working}
            onClick={() =>
              void run(
                () =>
                  operationsJobsApi.transitionPhase(
                    item.phase_id,
                    "completed",
                    "Repair completed by assigned field resource",
                  ),
                `${item.ticket_number} completed.`,
              )
            }
          >
            Mark Repair Complete
          </button>
        )}
      </div>
    </section>
  );
}

function EmployeeRow({
  a,
  editable,
  canManage,
  run,
}: {
  a: EmployeeAssignment;
  editable: boolean;
  canManage: boolean;
  run: Run;
}) {
  return (
    <div className="ops-crew-chip">
      <span>
        <strong>{a.employee_name}</strong>
        <small>
          Individual · {a.role ?? "Service technician"} · {dt(a.starts_at)} →{" "}
          {dt(a.ends_at)}
        </small>
      </span>
      {!a.crew_assignment_id && a.status === "assigned" && (
        <button
          disabled={!editable || !canManage}
          onClick={() =>
            void run(
              () =>
                operationalCoreApi.releaseEmployee(
                  a.id,
                  "Released from service call",
                ),
              "Employee released from service call.",
            )
          }
        >
          Release
        </button>
      )}
    </div>
  );
}

function Activity({
  phases,
  staffing,
  carriers,
}: {
  phases: JobPhase[];
  staffing: StaffingWorkspace | null;
  carriers: NationalCarrierDispatchItem[];
}) {
  return (
    <Card title="National Job Activity">
      <div className="ops-activity-list">
        {phases.map((p) => {
          const crews = assignedCrews(p, staffing),
            c = carrierFor(carriers, p.id),
            dispatchState = p.dispatch_planning_completed_at
              ? "Planning complete"
              : scheduled(p, staffing, carriers)
                ? "Scheduled"
                : "Needs schedule";
          return (
            <div
              className={`ops-activity-row ${p.status === "completed" ? "complete" : ""}`}
              key={p.id}
            >
              <span className={`ops-check ${terminal(p) ? "done" : ""}`}>
                {terminal(p) ? "✓" : ""}
              </span>
              <div className="ops-activity-main">
                <strong>{phaseLabel(p)}</strong>
                <small>
                  {p.scheduled_start && p.scheduled_end
                    ? `${dt(p.scheduled_start)} → ${dt(p.scheduled_end)}`
                    : "No Dispatch time set"}
                </small>
                <small>
                  {freightPhase(p)
                    ? c?.carrier_name
                      ? `Carrier: ${c.carrier_name}`
                      : "Carrier not entered"
                    : crews.length
                      ? crews.map((x) => x.crew_name).join(", ")
                      : p.phase_type === "on_rent"
                        ? "No dedicated crew required"
                        : "Crew not assigned"}
                </small>
              </div>
              <div className="ops-activity-state">
                <span>Dispatch</span>
                <strong>{dispatchState}</strong>
              </div>
              <div className="ops-activity-state">
                <span>Operations</span>
                <strong>
                  {phaseGroup(p)} {label(p.status)}
                </strong>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
